import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MaterialModule } from '@fullyops/shared/material.module';
import { I18NextCapPipe } from 'angular-i18next';
import {
  MomentDateAdapter,
  MAT_MOMENT_DATE_FORMATS,
} from '@angular/material-moment-adapter';
import { MAT_DATE_FORMATS, DateAdapter } from '@angular/material/core';
import { ZonedMomentDateAdapter } from '@fullyops/shared/zoned-moment-date-adapter';
import {
  LocationFormInitialValue,
  LocationInput,
  Location,
} from '@fullyops/data/locations/location';
import { Subscription } from 'rxjs';
import { MomentTimezoneToAngularTimezonePipe } from '@fullyops/shared/pipes/moment-timezone-to-angular-timezone.pipe';
import { RouterModule } from '@angular/router';
import { CountriesService } from '@fullyops/domain/countries/service';
import { AutocompletingSelectComponent } from '@fullyops/shared/components/autocompleting-select/component';
import { Company } from '@fullyops/data/companies/company';
import { LOCATION_FORM_TOKEN } from '@fullyops/features/locations/containers/location-list/location-list.component';
import { minuteOptions } from '@fullyops/shared/util/time';

export interface LocationFormInputs {
  initialValue: Location | null;
  showCompany: boolean;
  primaryLocationSwitchDisabled: boolean;
  onCancel: () => void;
  onSave: (location: Omit<LocationInput, 'companyId'>) => void;
  onDelete: () => void;
}

@Component({
  selector: 'fo-location-form',
  imports: [
    MaterialModule,
    ReactiveFormsModule,
    RouterModule,
    I18NextCapPipe,
    MomentTimezoneToAngularTimezonePipe,
    AutocompletingSelectComponent,
  ],
  templateUrl: './location-form.component.html',
  styleUrl: './location-form.component.scss',
  providers: [
    // MomentDateAdapter isn't providedIn: root, so we must inject it here so
    // that ZonedMomentDateAdapter can delegate to it.
    MomentDateAdapter,
    // Since we're not using MomentDateAdapter directly, we must specify the
    // date formats ourselves.
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    { provide: DateAdapter, useClass: ZonedMomentDateAdapter },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
  ],
})
export class LocationFormComponent implements OnInit, OnDestroy {
  @Input() disabled = false;
  @Input() initialValue?: LocationFormInitialValue;
  @Input() company: Company;
  @Input() primaryLocationSwitchDisabled = false;
  @Output() onDelete = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<Omit<LocationInput, 'companyId'>>();

  constructor(
    protected countriesService: CountriesService,
    @Inject(LOCATION_FORM_TOKEN) protected inputs: LocationFormInputs,
  ) {}

  get dirty() {
    return this.formGroup.dirty;
  }

  get valid() {
    return this.formGroup.valid;
  }

  private sub = new Subscription();

  ngOnInit(): void {
    this.countriesService.refreshAll();
    const v = this.initialValue ?? this.inputs?.initialValue;
    if (v != null) {
      this.sub.add(
        this.countries$.subscribe((countries) => {
          if (countries && countries.length > 0) {
            this.formGroup.patchValue(v, { emitEvent: false });
          }
        }),
      );
    }

    if (this.disabled) {
      this.formGroup.disable({ emitEvent: false });
    }

    if (
      this.primaryLocationSwitchDisabled ||
      this.inputs?.primaryLocationSwitchDisabled
    ) {
      this.formGroup.get('primary')?.disable({ emitEvent: false });
    }
  }

  delete() {
    this.onDelete.emit();
    this.inputs?.onDelete();
  }

  save() {
    if (this.formGroup.valid) {
      const locationFormValue = this.formGroup.getRawValue();
      this.onSave.emit(locationFormValue);
      this.inputs?.onSave(locationFormValue);
    } else {
      this.formGroup.markAllAsTouched();
    }
  }

  clear(): void {
    this.formGroup.reset(undefined, { emitEvent: false });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  protected formGroup = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: Validators.required,
    }),
    externalId: new FormControl('', { nonNullable: true }),
    primary: new FormControl(false, {
      nonNullable: true,
    }),
    streetName: new FormControl('', { nonNullable: true }),
    city: new FormControl('', { nonNullable: true }),
    postalCode: new FormControl('', { nonNullable: true }),
    countryId: new FormControl('', { nonNullable: true }),
    distance: new FormControl<number>(null),
    travelTime: new FormControl<number>(null),
  });

  protected countries$ = this.countriesService.countries$;
  protected travelTimeOptions = minuteOptions(15, 0, 1425);
}
